export type DeviceType = 'mobile' | 'tablet' | 'desktop'

const VALID_DEVICE_TYPES = new Set<DeviceType>(['mobile', 'tablet', 'desktop'])
const DEVICE_COOKIE_NAME = 'device'

// Android tablets send "Android" without "Mobile" in the UA string.
// Android phones always include "Mobile". iPadOS sends a macOS UA (handled
// client-side via maxTouchPoints in resolveDeviceType).
const TABLET_UA_REGEX = /iPad|(?=.*\bAndroid\b)(?!.*\bMobile\b)/i
const MOBILE_UA_REGEX =
  /Mobile|iPhone|iPod|Android|webOS|BlackBerry|Opera Mini|IEMobile/i

export function parseDeviceType(userAgent: string): DeviceType {
  if (!userAgent) return 'desktop'
  if (TABLET_UA_REGEX.test(userAgent)) return 'tablet'
  return MOBILE_UA_REGEX.test(userAgent) ? 'mobile' : 'desktop'
}

/**
 * Read the `device` cookie from a cookie header string.
 * Returns the device type if the cookie is present and valid, otherwise undefined.
 */
export function parseDeviceCookie(
  cookieHeader: string | undefined,
): DeviceType | undefined {
  if (!cookieHeader) return undefined
  const match = cookieHeader.match(
    new RegExp(`(?:^|;\\s*)${DEVICE_COOKIE_NAME}=([^;]+)`),
  )
  const value = match?.[1] as DeviceType | undefined
  return value && VALID_DEVICE_TYPES.has(value) ? value : undefined
}

/**
 * Determine device type from request: cookie takes precedence over UA parsing.
 * The cookie is set by the client after the first visit once the real device
 * type is known (e.g. iPadOS detected via maxTouchPoints). This eliminates
 * the hydration mismatch on subsequent page loads.
 */
export function getServerDeviceType(
  cookieHeader: string | undefined,
  userAgent: string,
): DeviceType {
  return parseDeviceCookie(cookieHeader) ?? parseDeviceType(userAgent)
}

/**
 * Set the device cookie from the client. Called once after hydration when the
 * resolved device type differs from what the server injected.
 * Max-age is 1 year; SameSite=Lax for normal navigation requests.
 */
export function setDeviceCookie(deviceType: DeviceType): void {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not a drop-in replacement for SSR + hydration cookie sync across browsers we support.
  document.cookie = `${DEVICE_COOKIE_NAME}=${deviceType};path=/;max-age=31536000;SameSite=Lax`
}

/**
 * Refine the server-determined device type on the client.
 *
 * iPadOS sends a macOS User-Agent, so the server can't distinguish iPad from Mac.
 * navigator.maxTouchPoints > 1 reliably identifies iPads (Macs report 0, or 1
 * for Force Touch trackpads). This runs once at hydration — not a listener.
 *
 * On the very first visit from iPadOS, the server sees a macOS UA and renders
 * "desktop". The client detects the mismatch, sets a `device` cookie, and React 19
 * recovers gracefully (brief flash). On all subsequent visits, the server reads the
 * cookie and SSRs the correct "tablet" layout — no mismatch.
 */
export function resolveDeviceType(serverDeviceType: DeviceType): DeviceType {
  if (serverDeviceType === 'mobile') return 'mobile'
  if (serverDeviceType === 'tablet') return 'tablet'
  if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) {
    return 'tablet'
  }
  return serverDeviceType
}

declare global {
  interface Window {
    __DEVICE_TYPE__?: DeviceType
  }
}
